<script lang="ts">
  import {
    FRACTIONS,
    LENGTH_FORMATS,
    MAX_PRECISION,
    UNITS,
    type LengthFormat,
    type Unit,
  } from '$lib/core/model';
  import type { EditorState } from '../editor-state.svelte';
  import { fieldKeys } from './field-keys';

  const { editor }: { editor: EditorState } = $props();

  const fractional = $derived(LENGTH_FORMATS[editor.settings.format].fractional);
</script>

<div class="group" role="group" aria-labelledby="document-units">
  <h3 id="document-units">Units</h3>
  <div class="row">
    <label class="field">
      Numbers in
      <select
        aria-label="Drawing unit"
        value={editor.settings.unit}
        onchange={(event) => {
          const select = event.currentTarget;
          void editor.setUnit(select.value as Unit).then(() => {
            select.value = editor.settings.unit;
          });
        }}
      >
        {#each Object.entries(UNITS) as [id, unit] (id)}
          <option value={id}>{unit.label}</option>
        {/each}
      </select>
    </label>
    <label class="field">
      Show as
      <select
        aria-label="Length format"
        value={editor.settings.format}
        onchange={(event) => editor.setLengthFormat(event.currentTarget.value as LengthFormat)}
      >
        {#each Object.entries(LENGTH_FORMATS) as [id, format] (id)}
          <option value={id}>{format.label}</option>
        {/each}
      </select>
    </label>
    {#if fractional}
      <label class="field">
        Fraction
        <select
          aria-label="Smallest fraction"
          value={editor.settings.fraction}
          onchange={(event) => editor.setFraction(Number(event.currentTarget.value))}
        >
          {#each FRACTIONS as fraction (fraction)}
            <option value={fraction}>1/{fraction}</option>
          {/each}
        </select>
      </label>
    {:else}
      <label class="field">
        Precision
        <input
          type="number"
          aria-label="Precision"
          min="0"
          max={MAX_PRECISION}
          step="1"
          value={editor.settings.precision}
          onkeydown={fieldKeys(String(editor.settings.precision))}
          onchange={(event) => {
            if (editor.setPrecision(Number(event.currentTarget.value)) !== null)
              event.currentTarget.value = String(editor.settings.precision);
          }}
        />
      </label>
    {/if}
    <label>
      <input
        type="checkbox"
        checked={editor.settings.dimensionUnits}
        onchange={(event) => editor.setDimensionUnits(event.currentTarget.checked)}
      />
      Units on dimensions
    </label>
  </div>
</div>

<div class="group" role="group" aria-labelledby="document-grid">
  <h3 id="document-grid">Grid</h3>
  <div class="row">
    <label class="field">
      Size
      <input
        type="number"
        aria-label="Grid size"
        min="0.001"
        step="1"
        value={editor.snapSettings.gridSize}
        onkeydown={fieldKeys(String(editor.snapSettings.gridSize))}
        onchange={(event) => editor.setGridSize(Number(event.currentTarget.value))}
      />
    </label>
    <label>
      <input type="checkbox" bind:checked={editor.showGrid} />
      Show grid
    </label>
  </div>
</div>

<div class="group" role="group" aria-labelledby="document-views">
  <h3 id="document-views">Views</h3>
  <div class="row">
    <label class="field">
      Margin
      <input
        type="number"
        aria-label="View margin"
        min="0"
        step="1"
        value={editor.settings.viewMargin}
        onkeydown={fieldKeys(String(editor.settings.viewMargin))}
        onchange={(event) => {
          if (editor.setViewMargin(Number(event.currentTarget.value)) !== null)
            event.currentTarget.value = String(editor.settings.viewMargin);
        }}
      />
    </label>
  </div>
</div>

<style>
  .group + .group {
    margin-top: var(--space-3);
  }

  h3 {
    margin: 0 0 var(--space-2);
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .row {
    display: flex;
    align-items: flex-end;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 0.8125rem;
  }

  .field {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }

  input[type='number'],
  select {
    width: 6rem;
    padding: 4px 6px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font: inherit;
    font-size: 0.8125rem;
  }

  select {
    width: 9rem;
  }
</style>
